<script setup>
import { ref, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import MultiListField from './MultiListField.vue';
import MultiCheckboxField from './MultiCheckboxField.vue';
import RadioField from './RadioField.vue';
import FileField from './FileField.vue';
import KanbanDatePicker from './KanbanDatePicker.vue';

const props = defineProps({
  attributes: { type: Array, default: () => [] },
  customAttributes: { type: Object, default: () => ({}) },
  folderName: { type: String, default: '' },
  cardId: { type: Number, default: null },
});
const emit = defineEmits(['update']);
// Helpers para suportar attribute_values como [{label, key}] ou string legada
function optKey(opt) {
  return typeof opt === 'object' ? opt.key : opt;
}
function optLabel(opt) {
  return typeof opt === 'object' ? opt.label : opt;
}
function getOptLabel(values, key) {
  if (!key) return '';
  const found = (values ?? []).find(o => optKey(o) === key);
  return found ? optLabel(found) : key;
}

const { t } = useI18n();

function getVal(key) {
  return props.customAttributes[key] ?? '';
}

function setVal(key, val) {
  emit('update', { ...props.customAttributes, [key]: val });
}

// --- dropdown de lista simples ---
const openListKey = ref(null);
const openCurrencyKey = ref(null);

function toggleListDropdown(key) {
  openListKey.value = openListKey.value === key ? null : key;
}
function selectListOption(key, val) {
  setVal(key, val);
  openListKey.value = null;
}
function closeAllDropdowns() {
  openListKey.value = null;
  openCurrencyKey.value = null;
}

// --- edição inline ---
const editingKey = ref(null);
const editRefs = {};
const fieldError = ref({});

function setEditRef(key, el) {
  if (el) editRefs[key] = el;
  else delete editRefs[key];
}

function getEditRef(key) {
  return editRefs[key] ?? null;
}

function focusEnd(el) {
  el.focus();
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

async function startEditingPhone(key) {
  const stored = getVal(key) ?? '';
  const { ddi, local } = extractDdiAndLocal(stored);
  phoneDdi.value[key] = ddi;
  editingKey.value = key;
  await nextTick();
  const el = getEditRef(key);
  if (el) {
    el.innerText = local;
    focusEnd(el);
  }
}

async function startEditing(key, initialValue) {
  editingKey.value = key;
  await nextTick();
  const el = getEditRef(key);
  if (el) {
    el.innerText = initialValue ?? getVal(key) ?? '';
    focusEnd(el);
  }
}

function saveField(key, type, event) {
  const raw = (
    event?.target?.innerText ??
    getEditRef(key)?.innerText ??
    ''
  ).trim();
  fieldError.value[key] = '';

  if (
    type === 'number' &&
    raw !== '' &&
    Number.isNaN(Number(raw.replace(',', '.')))
  ) {
    fieldError.value[key] = 'Apenas números são permitidos';
    return;
  }
  if (
    type === 'percent' &&
    raw !== '' &&
    Number.isNaN(Number(raw.replace(',', '.')))
  ) {
    fieldError.value[key] = 'Apenas números são permitidos';
    return;
  }
  if (
    type === 'email' &&
    raw !== '' &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)
  ) {
    fieldError.value[key] = 'E-mail inválido';
    return;
  }
  if (
    type === 'link' &&
    raw !== '' &&
    !/^https?:\/\/.+\..+/.test(raw) &&
    !raw.includes('.')
  ) {
    fieldError.value[key] = 'Link inválido (ex: https://site.com)';
    return;
  }

  editingKey.value = null;
  setVal(key, raw);
}

// --- telefone ---
const DDI_LIST = [
  { code: '+55', flag: '🇧🇷', label: 'Brasil' },
  { code: '+1', flag: '🇺🇸', label: 'EUA' },
  { code: '+351', flag: '🇵🇹', label: 'Portugal' },
  { code: '+54', flag: '🇦🇷', label: 'Argentina' },
  { code: '+34', flag: '🇪🇸', label: 'Espanha' },
  { code: '+52', flag: '🇲🇽', label: 'México' },
  { code: '+57', flag: '🇨🇴', label: 'Colômbia' },
  { code: '+56', flag: '🇨🇱', label: 'Chile' },
  { code: '+598', flag: '🇺🇾', label: 'Uruguai' },
  { code: '+595', flag: '🇵🇾', label: 'Paraguai' },
  { code: '+51', flag: '🇵🇪', label: 'Peru' },
  { code: '+44', flag: '🇬🇧', label: 'Reino Unido' },
  { code: '+49', flag: '🇩🇪', label: 'Alemanha' },
  { code: '+33', flag: '🇫🇷', label: 'França' },
  { code: '+39', flag: '🇮🇹', label: 'Itália' },
];

// Estado DDI por campo (chave → código DDI selecionado)
const phoneDdi = ref({});
const phoneDdiOpen = ref({});

function getPhoneDdi(key) {
  return phoneDdi.value[key] ?? '+55';
}

function setPhoneDdi(key, code) {
  phoneDdi.value[key] = code;
  phoneDdiOpen.value[key] = false;
}

function toggleDdiDropdown(key) {
  phoneDdiOpen.value[key] = !phoneDdiOpen.value[key];
}

function extractDdiAndLocal(stored) {
  if (!stored) return { ddi: '+55', local: '' };
  const match = DDI_LIST.find(d => stored.startsWith(d.code + ' '));
  if (match)
    return { ddi: match.code, local: stored.slice(match.code.length + 1) };
  // fallback: sem DDI conhecido
  return { ddi: '+55', local: stored };
}

function formatPhoneLocal(val) {
  const digits = String(val).replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length === 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function formatPhone(val) {
  const { ddi, local } = extractDdiAndLocal(String(val ?? ''));
  const formatted = formatPhoneLocal(local);
  return formatted ? `${ddi} ${formatted}` : '';
}

// --- CPF / CNPJ ---
function formatCpf(val) {
  const digits = String(val).replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
}

function formatCnpj(val) {
  const digits = String(val).replace(/\D/g, '').slice(0, 14);
  if (!digits) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

function maskInput(el, formatter) {
  const raw = el.innerText;
  const formatted = formatter(raw);
  if (formatted === raw) return;
  el.innerText = formatted;
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

// --- monetário ---
const CURRENCIES = [
  { symbol: 'R$', code: 'BRL' },
  { symbol: '$', code: 'USD' },
  { symbol: '€', code: 'EUR' },
];

function getCurrencySymbol(key) {
  const stored = getVal(key);
  if (!stored || typeof stored !== 'object') return 'R$';
  return stored.currency ?? 'R$';
}

function getCurrencyAmount(key) {
  const stored = getVal(key);
  if (!stored || typeof stored !== 'object') return stored ?? '';
  return stored.amount ?? '';
}

function formatCurrency(amount) {
  if (amount === '' || amount === null || amount === undefined) return '';
  const num = parseFloat(String(amount).replace(',', '.'));
  if (Number.isNaN(num)) return amount;
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Acumula apenas os dígitos digitados para a máscara monetária
const currencyRawDigits = ref({});

function formatCurrencyMask(digits) {
  if (!digits) return '';
  const padded = digits.padStart(3, '0');
  const intPart = padded.slice(0, -2).replace(/^0+/, '') || '0';
  const decPart = padded.slice(-2);
  const intFormatted = Number(intPart).toLocaleString('pt-BR');
  return `${intFormatted},${decPart}`;
}

function onCurrencyInput(key, el) {
  const text = el.innerText ?? '';
  const digits = text.replace(/\D/g, '');
  currencyRawDigits.value[key] = digits;
  const formatted = formatCurrencyMask(digits);
  el.innerText = formatted;
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

function startCurrencyEditing(key, amount) {
  const digits = amount
    ? String(Math.round(parseFloat(amount) * 100)).padStart(1, '0')
    : '';
  currencyRawDigits.value[key] = digits;
  startEditing(key, formatCurrencyMask(digits));
}

function saveCurrencyAmount(key) {
  const digits = currencyRawDigits.value[key] ?? '';
  const num = digits ? parseInt(digits, 10) / 100 : NaN;
  const symbol = getCurrencySymbol(key);
  setVal(key, { currency: symbol, amount: Number.isNaN(num) ? '' : num });
  delete currencyRawDigits.value[key];
  editingKey.value = null;
}

function selectCurrency(key, symbol) {
  const amount = getCurrencyAmount(key);
  setVal(key, { currency: symbol, amount });
  openCurrencyKey.value = null;
}

// --- datepicker customizado ---
const openDateKey = ref(null);
const datePickerStyle = ref({});
const dateTriggerRefs = ref({});

function openDatePicker(key, triggerEl) {
  if (openDateKey.value === key) {
    openDateKey.value = null;
    return;
  }
  if (triggerEl) {
    const rect = triggerEl.getBoundingClientRect();
    const pickerHeight = 390;
    const goUp = rect.bottom + pickerHeight > window.innerHeight;
    datePickerStyle.value = {
      position: 'fixed',
      left: `${rect.left}px`,
      zIndex: 200,
      ...(goUp
        ? { bottom: `${window.innerHeight - rect.top + 4}px` }
        : { top: `${rect.bottom + 4}px` }),
    };
  }
  openDateKey.value = key;
}

function getDatePart(key) {
  const val = getVal(key);
  if (!val) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
  return null;
}

function getTimePart(key) {
  const val = getVal(key);
  if (!val) return null;
  if (/T\d{2}:\d{2}/.test(val)) return val.slice(11, 16);
  return null;
}

function onDateUpdate(key, date) {
  const time = getTimePart(key);
  if (!date) {
    setVal(key, null);
    openDateKey.value = null;
    return;
  }
  setVal(key, time ? `${date}T${time}` : date);
}

function onTimeUpdate(key, time) {
  const date = getDatePart(key);
  if (!date) return;
  setVal(key, time ? `${date}T${time}` : date);
}

// --- data ---
function formatDateDisplay(val) {
  if (!val) return '';
  // val pode ser "2026-07-10T14:55" ou "2026-07-10" ou "14:55"
  if (/^\d{2}:\d{2}/.test(val)) return val; // só hora
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split('-');
    return `${d}/${m}/${String(y).slice(2)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val)) {
    const [datePart, timePart] = val.split('T');
    const [y, m, d] = datePart.split('-');
    return `${d}/${m}/${String(y).slice(2)} às ${timePart.slice(0, 5)}`;
  }
  return val;
}

// --- link ---
function ensureHttp(url) {
  if (!url) return '';
  if (/^https?:\/\//.test(url)) return url;
  return `https://${url}`;
}

onMounted(() => window.addEventListener('click', closeAllDropdowns));
onUnmounted(() => window.removeEventListener('click', closeAllDropdowns));

const viewCls =
  'text-sm text-left px-2 py-1 rounded bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 min-h-[30px] w-full overflow-hidden outline-none';
const viewClsFlexible =
  'text-sm text-left px-2 py-1 rounded bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 min-h-[30px] overflow-hidden outline-none truncate flex-1 min-w-0';
const viewClsSingleLine = viewCls + ' truncate';
const viewClsMultiLine = viewCls + ' whitespace-pre-wrap break-all';
const editCls =
  'text-sm text-left px-2 py-1 rounded bg-n-alpha-2 text-n-slate-12 min-h-[30px] w-full outline-none break-all overflow-hidden';
const editStyle =
  'min-height: 30px; box-shadow: inset 0 -1px 0 0 #2781F6; word-break: break-all; overflow-wrap: break-word;';
</script>

<template>
  <div class="flex flex-col gap-3 py-2">
    <h4
      v-if="folderName"
      class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
    >
      {{ folderName }}
    </h4>
    <div
      v-if="!attributes.length"
      class="flex flex-col items-center justify-center py-12 gap-3 text-n-slate-9"
    >
      <span class="i-lucide-folder-open size-8" />
      <span class="text-sm">{{ t('KANBAN.ADVANCED_FILTERS.EMPTY') }}</span>
    </div>

    <div class="grid grid-cols-2 gap-3">
      <div
        v-for="attr in attributes"
        :key="attr.attribute_key"
        class="flex flex-col gap-1 min-w-0"
        :class="attr.half_width ? 'col-span-1' : 'col-span-2'"
      >
        <!-- Label -->
        <div class="relative group/desc">
          <span class="text-xs font-medium text-n-slate-10">{{
            attr.attribute_display_name
          }}</span>
          <div
            v-if="attr.attribute_description"
            class="absolute left-0 bottom-6 z-20 hidden group-hover/desc:block bg-n-surface-2 border border-n-weak text-n-slate-12 text-xs rounded px-2.5 py-1.5 w-52 whitespace-pre-line shadow-xl leading-relaxed pointer-events-none"
          >
            {{ attr.attribute_description }}
          </div>
        </div>

        <!-- Texto normal -->
        <div v-if="attr.attribute_display_type === 'text'" class="w-full">
          <div
            v-if="editingKey === attr.attribute_key"
            :ref="el => setEditRef(attr.attribute_key, el)"
            contenteditable="true"
            :class="editCls"
            :style="editStyle"
            @blur="saveField(attr.attribute_key, 'text', $event)"
            @keydown.enter.prevent="
              saveField(attr.attribute_key, 'text', $event)
            "
            @keydown.esc="editingKey.value = null"
          />
          <button
            v-else
            :class="[
              viewClsSingleLine,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="startEditing(attr.attribute_key)"
          >
            {{ getVal(attr.attribute_key) || '—' }}
          </button>
        </div>

        <!-- Texto longo -->
        <div
          v-else-if="attr.attribute_display_type === 'text_area'"
          class="w-full"
        >
          <div
            v-if="editingKey === attr.attribute_key"
            :ref="el => setEditRef(attr.attribute_key, el)"
            contenteditable="true"
            :class="editCls"
            :style="editStyle + ' white-space: pre-wrap;'"
            @blur="saveField(attr.attribute_key, 'text_area', $event)"
            @keydown.esc="editingKey.value = null"
          />
          <button
            v-else
            :class="[
              viewClsMultiLine,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="startEditing(attr.attribute_key)"
          >
            {{ getVal(attr.attribute_key) || '—' }}
          </button>
        </div>

        <!-- Lista de texto -->
        <div
          v-else-if="attr.attribute_display_type === 'text_list'"
          class="flex flex-col gap-2"
        >
          <div
            v-for="item in attr.attribute_values ?? []"
            :key="item.key ?? item"
            class="flex flex-col gap-0.5"
          >
            <span class="text-xs text-n-slate-10">{{
              item.label ?? item
            }}</span>
            <div
              v-if="
                editingKey === attr.attribute_key + '__' + (item.key ?? item)
              "
              :ref="
                el =>
                  setEditRef(attr.attribute_key + '__' + (item.key ?? item), el)
              "
              contenteditable="true"
              :class="editCls"
              :style="editStyle"
              @blur="
                e => {
                  const raw = e.target.innerText.trim();
                  if (
                    attr.regex_pattern &&
                    !new RegExp(attr.regex_pattern).test(raw) &&
                    raw !== ''
                  ) {
                    e.target.innerText =
                      (getVal(attr.attribute_key) || {})[item.key ?? item] ??
                      '';
                    editingKey.value = null;
                    return;
                  }
                  setVal(attr.attribute_key, {
                    ...(getVal(attr.attribute_key) || {}),
                    [item.key ?? item]: raw,
                  });
                  editingKey.value = null;
                }
              "
              @keydown="
                e => {
                  if (e.key === 'Escape') {
                    editingKey.value = null;
                    return;
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const raw = e.target.innerText.trim();
                    if (
                      attr.regex_pattern &&
                      !new RegExp(attr.regex_pattern).test(raw) &&
                      raw !== ''
                    ) {
                      e.target.innerText =
                        (getVal(attr.attribute_key) || {})[item.key ?? item] ??
                        '';
                      editingKey.value = null;
                      return;
                    }
                    setVal(attr.attribute_key, {
                      ...(getVal(attr.attribute_key) || {}),
                      [item.key ?? item]: raw,
                    });
                    e.target.blur();
                    return;
                  }
                  if (!attr.regex_pattern) return;
                  const allowed = [
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    'Home',
                    'End',
                    'Tab',
                  ];
                  if (allowed.includes(e.key)) return;
                  if (e.ctrlKey || e.metaKey) return;
                  if (!/^\d$/.test(e.key)) e.preventDefault();
                }
              "
            />
            <button
              v-else
              :class="[
                viewClsSingleLine,
                {
                  'text-n-slate-9': !(getVal(attr.attribute_key) || {})[
                    item.key ?? item
                  ],
                },
              ]"
              @click="
                startEditing(
                  attr.attribute_key + '__' + (item.key ?? item),
                  (getVal(attr.attribute_key) || {})[item.key ?? item]
                )
              "
            >
              {{ (getVal(attr.attribute_key) || {})[item.key ?? item] || '—' }}
            </button>
          </div>
        </div>

        <!-- Campos (texto longo) -->
        <div
          v-else-if="attr.attribute_display_type === 'text_list_area'"
          class="flex flex-col gap-2"
        >
          <div
            v-for="item in attr.attribute_values ?? []"
            :key="item.key ?? item"
            class="flex flex-col gap-0.5"
          >
            <span class="text-xs text-n-slate-10">{{
              item.label ?? item
            }}</span>
            <div
              v-if="
                editingKey === attr.attribute_key + '__' + (item.key ?? item)
              "
              :ref="
                el =>
                  setEditRef(attr.attribute_key + '__' + (item.key ?? item), el)
              "
              contenteditable="true"
              :class="editCls"
              :style="editStyle + ' white-space: pre-wrap;'"
              @blur="
                e => {
                  setVal(attr.attribute_key, {
                    ...(getVal(attr.attribute_key) || {}),
                    [item.key ?? item]: e.target.innerText.trim(),
                  });
                  editingKey.value = null;
                }
              "
              @keydown.esc="editingKey.value = null"
            />
            <button
              v-else
              :class="[
                viewClsMultiLine,
                {
                  'text-n-slate-9': !(getVal(attr.attribute_key) || {})[
                    item.key ?? item
                  ],
                },
              ]"
              @click="
                startEditing(
                  attr.attribute_key + '__' + (item.key ?? item),
                  (getVal(attr.attribute_key) || {})[item.key ?? item]
                )
              "
            >
              {{ (getVal(attr.attribute_key) || {})[item.key ?? item] || '—' }}
            </button>
          </div>
        </div>

        <!-- Número -->
        <div
          v-else-if="attr.attribute_display_type === 'number'"
          class="w-full"
        >
          <div
            v-if="editingKey === attr.attribute_key"
            :ref="el => setEditRef(attr.attribute_key, el)"
            contenteditable="true"
            inputmode="numeric"
            :class="editCls"
            :style="editStyle"
            @blur="saveField(attr.attribute_key, 'number', $event)"
            @keydown.enter.prevent="
              saveField(attr.attribute_key, 'number', $event)
            "
            @keydown.esc="editingKey.value = null"
            @keypress="
              e => {
                if (!/[\d.,\-]/.test(e.key)) e.preventDefault();
              }
            "
          />
          <button
            v-else
            :class="[
              viewCls,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="startEditing(attr.attribute_key)"
          >
            {{ getVal(attr.attribute_key) || '—' }}
          </button>
          <p
            v-if="fieldError[attr.attribute_key]"
            class="text-xs text-n-ruby-11 mt-0.5"
          >
            {{ fieldError[attr.attribute_key] }}
          </p>
        </div>

        <!-- Telefone -->
        <div v-else-if="attr.attribute_display_type === 'phone'" class="w-full">
          <div
            v-if="editingKey === attr.attribute_key"
            class="flex items-center gap-1 relative"
            @click.stop
          >
            <!-- DDI dropdown -->
            <div class="relative flex-shrink-0">
              <button
                type="button"
                class="flex items-center gap-0.5 text-xs text-n-slate-11 hover:text-n-slate-12 focus:outline-none"
                @mousedown.prevent="toggleDdiDropdown(attr.attribute_key)"
              >
                <span>{{
                  DDI_LIST.find(d => d.code === getPhoneDdi(attr.attribute_key))
                    ?.flag
                }}</span>
                <span>{{ getPhoneDdi(attr.attribute_key) }}</span>
                <span class="i-lucide-chevron-down size-2.5" />
              </button>
              <div
                v-if="phoneDdiOpen[attr.attribute_key]"
                class="absolute left-0 top-full mt-1 z-[300] bg-n-background border border-n-weak rounded-xl shadow-xl py-1 w-44"
                @mousedown.prevent
              >
                <button
                  v-for="ddi in DDI_LIST"
                  :key="ddi.code"
                  type="button"
                  class="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors text-n-slate-12"
                  :class="
                    ddi.code === getPhoneDdi(attr.attribute_key)
                      ? 'text-n-brand font-medium'
                      : ''
                  "
                  @mousedown.prevent="setPhoneDdi(attr.attribute_key, ddi.code)"
                >
                  <span>{{ ddi.flag }}</span>
                  <span class="flex-1 text-left">{{ ddi.label }}</span>
                  <span class="text-n-slate-10">{{ ddi.code }}</span>
                </button>
              </div>
            </div>
            <!-- Campo local -->
            <div
              :ref="el => setEditRef(attr.attribute_key, el)"
              contenteditable="true"
              inputmode="tel"
              class="flex-1"
              :class="editCls"
              :style="editStyle"
              @blur="
                e => {
                  const local = formatPhoneLocal(e.target.innerText.trim());
                  setVal(
                    attr.attribute_key,
                    local ? `${getPhoneDdi(attr.attribute_key)} ${local}` : ''
                  );
                  editingKey.value = null;
                }
              "
              @keydown.enter.prevent="
                e => {
                  const local = formatPhoneLocal(e.target.innerText.trim());
                  setVal(
                    attr.attribute_key,
                    local ? `${getPhoneDdi(attr.attribute_key)} ${local}` : ''
                  );
                  editingKey.value = null;
                }
              "
              @keydown.esc="editingKey.value = null"
              @keypress="
                e => {
                  if (!/\d/.test(e.key)) e.preventDefault();
                }
              "
              @input="e => maskInput(e.target, formatPhoneLocal)"
            />
          </div>
          <div v-else class="flex items-center gap-1 w-full group/phone-field">
            <button
              :class="[
                viewClsFlexible,
                { 'text-n-slate-9': !getVal(attr.attribute_key) },
              ]"
              @click="startEditingPhone(attr.attribute_key)"
            >
              <template v-if="getVal(attr.attribute_key)">
                <span class="mr-2">{{
                  DDI_LIST.find(d =>
                    getVal(attr.attribute_key).startsWith(d.code + ' ')
                  )?.flag ?? '🇧🇷'
                }}</span>{{ extractDdiAndLocal(getVal(attr.attribute_key)).local }}
              </template>
              <template v-else>—</template>
            </button>
            <a
              v-if="getVal(attr.attribute_key)"
              :href="`https://wa.me/${getVal(attr.attribute_key).replace(/\D/g, '').startsWith('55') ? getVal(attr.attribute_key).replace(/\D/g, '') : '55' + getVal(attr.attribute_key).replace(/\D/g, '')}`"
              target="_blank"
              rel="noopener noreferrer"
              class="flex-shrink-0 text-n-slate-9 hover:text-n-teal-10 opacity-0 group-hover/phone-field:opacity-100 max-w-0 group-hover/phone-field:max-w-[20px] overflow-hidden transition-all duration-200"
              title="Abrir no WhatsApp"
              @click.stop
            >
              <span class="i-lucide-arrow-up-right size-3.5" />
            </a>
          </div>
        </div>

        <!-- CPF -->
        <div v-else-if="attr.attribute_display_type === 'cpf'" class="w-full">
          <div
            v-if="editingKey === attr.attribute_key"
            :ref="el => setEditRef(attr.attribute_key, el)"
            contenteditable="true"
            inputmode="numeric"
            :class="editCls"
            :style="editStyle"
            @blur="
              e => {
                setVal(
                  attr.attribute_key,
                  formatCpf(e.target.innerText.trim())
                );
                editingKey.value = null;
              }
            "
            @keydown.enter.prevent="
              e => {
                setVal(
                  attr.attribute_key,
                  formatCpf(e.target.innerText.trim())
                );
                editingKey.value = null;
              }
            "
            @keydown.esc="editingKey.value = null"
            @keypress="
              e => {
                if (!/\d/.test(e.key)) e.preventDefault();
              }
            "
            @input="e => maskInput(e.target, formatCpf)"
          />
          <button
            v-else
            :class="[
              viewCls,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="startEditing(attr.attribute_key)"
          >
            {{ getVal(attr.attribute_key) || '—' }}
          </button>
        </div>

        <!-- CNPJ -->
        <div v-else-if="attr.attribute_display_type === 'cnpj'" class="w-full">
          <div
            v-if="editingKey === attr.attribute_key"
            :ref="el => setEditRef(attr.attribute_key, el)"
            contenteditable="true"
            inputmode="numeric"
            :class="editCls"
            :style="editStyle"
            @blur="
              e => {
                setVal(
                  attr.attribute_key,
                  formatCnpj(e.target.innerText.trim())
                );
                editingKey.value = null;
              }
            "
            @keydown.enter.prevent="
              e => {
                setVal(
                  attr.attribute_key,
                  formatCnpj(e.target.innerText.trim())
                );
                editingKey.value = null;
              }
            "
            @keydown.esc="editingKey.value = null"
            @keypress="
              e => {
                if (!/\d/.test(e.key)) e.preventDefault();
              }
            "
            @input="e => maskInput(e.target, formatCnpj)"
          />
          <button
            v-else
            :class="[
              viewCls,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="startEditing(attr.attribute_key)"
          >
            {{ getVal(attr.attribute_key) || '—' }}
          </button>
        </div>

        <!-- E-mail -->
        <div v-else-if="attr.attribute_display_type === 'email'" class="w-full">
          <div
            v-if="editingKey === attr.attribute_key"
            :ref="el => setEditRef(attr.attribute_key, el)"
            contenteditable="true"
            inputmode="email"
            :class="editCls"
            :style="editStyle"
            @blur="saveField(attr.attribute_key, 'email', $event)"
            @keydown.enter.prevent="
              saveField(attr.attribute_key, 'email', $event)
            "
            @keydown.esc="editingKey.value = null"
          />
          <button
            v-else
            :class="[
              viewCls,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="startEditing(attr.attribute_key)"
          >
            <span class="block truncate">{{
              getVal(attr.attribute_key) || '—'
            }}</span>
          </button>
          <p
            v-if="fieldError[attr.attribute_key]"
            class="text-xs text-n-ruby-11 mt-0.5"
          >
            {{ fieldError[attr.attribute_key] }}
          </p>
        </div>

        <!-- Percentual -->
        <div
          v-else-if="attr.attribute_display_type === 'percent'"
          class="w-full"
        >
          <div
            v-if="editingKey === attr.attribute_key"
            class="flex items-center gap-1"
          >
            <div
              :ref="el => setEditRef(attr.attribute_key, el)"
              contenteditable="true"
              inputmode="decimal"
              :class="editCls"
              :style="editStyle"
              @blur="saveField(attr.attribute_key, 'percent', $event)"
              @keydown.enter.prevent="
                saveField(attr.attribute_key, 'percent', $event)
              "
              @keydown.esc="editingKey.value = null"
              @keypress="
                e => {
                  if (!/[\d.,\-]/.test(e.key)) e.preventDefault();
                }
              "
            />
            <span class="text-sm text-n-slate-10 flex-shrink-0">%</span>
          </div>
          <button
            v-else
            :class="[
              viewCls,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="startEditing(attr.attribute_key)"
          >
            {{
              getVal(attr.attribute_key)
                ? `${getVal(attr.attribute_key)} %`
                : '—'
            }}
          </button>
          <p
            v-if="fieldError[attr.attribute_key]"
            class="text-xs text-n-ruby-11 mt-0.5"
          >
            {{ fieldError[attr.attribute_key] }}
          </p>
        </div>

        <!-- Monetário -->
        <div
          v-else-if="attr.attribute_display_type === 'currency'"
          class="w-full relative"
          @click.stop
        >
          <!-- Modo edição -->
          <div
            v-if="editingKey === attr.attribute_key"
            class="flex items-center rounded bg-n-alpha-1 min-h-[30px] w-full overflow-hidden outline-none ring-1 ring-n-brand"
          >
            <button
              type="button"
              class="flex-shrink-0 flex items-center gap-0.5 pl-2.5 pr-1.5 h-full text-sm text-n-slate-10 hover:text-n-brand transition-colors"
              @click.stop="
                openCurrencyKey =
                  openCurrencyKey === attr.attribute_key
                    ? null
                    : attr.attribute_key
              "
            >
              {{ getCurrencySymbol(attr.attribute_key) }}
              <span class="i-lucide-chevron-down size-3" />
            </button>
            <div
              :ref="el => setEditRef(attr.attribute_key, el)"
              contenteditable="true"
              inputmode="numeric"
              class="flex-1 pr-2.5 py-1.5 text-sm text-n-slate-12 outline-none min-w-0"
              @blur="saveCurrencyAmount(attr.attribute_key)"
              @keydown.enter.prevent="saveCurrencyAmount(attr.attribute_key)"
              @keydown.esc="editingKey.value = null"
              @input="e => onCurrencyInput(attr.attribute_key, e.target)"
              @keypress="
                e => {
                  if (!/[\d]/.test(e.key)) e.preventDefault();
                }
              "
            />
          </div>
          <!-- Modo exibição -->
          <button
            v-else
            type="button"
            class="group w-full text-left px-2.5 py-1.5 rounded bg-n-alpha-1 hover:bg-n-alpha-2 min-h-[30px] text-sm"
            :class="
              getCurrencyAmount(attr.attribute_key)
                ? 'text-n-slate-12'
                : 'text-n-slate-9'
            "
            @click="
              startCurrencyEditing(
                attr.attribute_key,
                getCurrencyAmount(attr.attribute_key)
              )
            "
          >
            <span
              class="group-hover:text-n-brand transition-colors"
              @click.stop="
                openCurrencyKey =
                  openCurrencyKey === attr.attribute_key
                    ? null
                    : attr.attribute_key
              "
              >{{ getCurrencySymbol(attr.attribute_key) }}</span
            >
            {{
              getCurrencyAmount(attr.attribute_key)
                ? ` ${formatCurrency(getCurrencyAmount(attr.attribute_key))}`
                : ' —'
            }}
          </button>
          <!-- Dropdown de moeda -->
          <div
            v-if="openCurrencyKey === attr.attribute_key"
            class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-[80px]"
          >
            <button
              v-for="c in CURRENCIES"
              :key="c.code"
              type="button"
              class="w-full text-left px-3 py-1.5 text-sm hover:bg-n-alpha-1 transition-colors"
              :class="
                getCurrencySymbol(attr.attribute_key) === c.symbol
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="selectCurrency(attr.attribute_key, c.symbol)"
            >
              {{ c.symbol }}
            </button>
          </div>
        </div>

        <!-- Data -->
        <div
          v-else-if="attr.attribute_display_type === 'date'"
          class="w-full relative"
          @click.stop
        >
          <button
            :ref="
              el => {
                if (el) dateTriggerRefs[attr.attribute_key] = el;
              }
            "
            :class="[
              viewCls,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="
              openDatePicker(
                attr.attribute_key,
                dateTriggerRefs[attr.attribute_key]
              )
            "
          >
            {{ formatDateDisplay(getVal(attr.attribute_key)) || '—' }}
          </button>
          <div
            v-if="openDateKey === attr.attribute_key"
            class="fixed inset-0 z-[199]"
            @click="openDateKey = null"
          />
          <div
            v-if="openDateKey === attr.attribute_key"
            :style="datePickerStyle"
          >
            <KanbanDatePicker
              :date="getDatePart(attr.attribute_key)"
              :time="getTimePart(attr.attribute_key)"
              @update:date="onDateUpdate(attr.attribute_key, $event)"
              @update:time="onTimeUpdate(attr.attribute_key, $event)"
              @close="openDateKey = null"
            />
          </div>
        </div>

        <!-- Link -->
        <div v-else-if="attr.attribute_display_type === 'link'" class="w-full">
          <div
            v-if="editingKey === attr.attribute_key"
            :ref="el => setEditRef(attr.attribute_key, el)"
            contenteditable="true"
            inputmode="url"
            :class="editCls"
            :style="editStyle"
            @blur="saveField(attr.attribute_key, 'link', $event)"
            @keydown.enter.prevent="
              saveField(attr.attribute_key, 'link', $event)
            "
            @keydown.esc="editingKey.value = null"
          />
          <div v-else class="flex items-center gap-1 w-full group/link-field">
            <button
              :class="[
                viewClsFlexible,
                { 'text-n-slate-9': !getVal(attr.attribute_key) },
              ]"
              @click="startEditing(attr.attribute_key)"
            >
              {{ getVal(attr.attribute_key) || '—' }}
            </button>
            <a
              v-if="getVal(attr.attribute_key)"
              :href="ensureHttp(getVal(attr.attribute_key))"
              target="_blank"
              rel="noopener noreferrer"
              class="flex-shrink-0 text-n-slate-9 hover:text-n-brand opacity-0 group-hover/link-field:opacity-100 max-w-0 group-hover/link-field:max-w-[20px] overflow-hidden transition-all duration-200"
              title="Abrir link"
              @click.stop
            >
              <span class="i-lucide-arrow-up-right size-3.5" />
            </a>
          </div>
          <p
            v-if="fieldError[attr.attribute_key]"
            class="text-xs text-n-ruby-11 mt-0.5"
          >
            {{ fieldError[attr.attribute_key] }}
          </p>
        </div>

        <!-- Arquivo -->
        <FileField
          v-else-if="attr.attribute_display_type === 'file'"
          :card-id="cardId"
          :model-value="getVal(attr.attribute_key)"
          @update:model-value="setVal(attr.attribute_key, $event)"
        />

        <!-- Lista simples -->
        <div
          v-else-if="attr.attribute_display_type === 'list'"
          class="relative"
          @click.stop
        >
          <button
            type="button"
            class="flex items-center justify-between gap-1"
            :class="[
              viewCls,
              { 'text-n-slate-9': !getVal(attr.attribute_key) },
            ]"
            @click="toggleListDropdown(attr.attribute_key)"
          >
            <span class="truncate">{{
              getOptLabel(attr.attribute_values, getVal(attr.attribute_key)) ||
              '—'
            }}</span>
            <span
              class="i-lucide-chevron-down size-3 text-n-slate-10 flex-shrink-0"
            />
          </button>
          <div
            v-if="openListKey === attr.attribute_key"
            class="absolute left-0 top-full mt-1 z-30 w-full bg-n-background border border-n-weak rounded-xl shadow-xl py-1"
          >
            <button
              type="button"
              class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-10"
              @click="selectListOption(attr.attribute_key, '')"
            >
              —
            </button>
            <button
              v-for="opt in attr.attribute_values ?? []"
              :key="optKey(opt)"
              type="button"
              class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
              :class="
                getVal(attr.attribute_key) === optKey(opt)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="selectListOption(attr.attribute_key, optKey(opt))"
            >
              {{ optLabel(opt) }}
            </button>
          </div>
        </div>

        <!-- Lista múltipla -->
        <div
          v-else-if="attr.attribute_display_type === 'multi_list'"
          @click.stop
        >
          <MultiListField
            :values="attr.attribute_values ?? []"
            :model-value="getVal(attr.attribute_key) || []"
            @update:model-value="setVal(attr.attribute_key, $event)"
          />
        </div>

        <!-- Caixa de seleção simples -->
        <div
          v-else-if="attr.attribute_display_type === 'checkbox'"
          class="flex items-center pt-1"
        >
          <input
            type="checkbox"
            :checked="!!getVal(attr.attribute_key)"
            class="w-4 h-4 accent-n-brand cursor-pointer"
            @change="setVal(attr.attribute_key, $event.target.checked)"
          />
        </div>

        <!-- Caixa múltipla -->
        <MultiCheckboxField
          v-else-if="attr.attribute_display_type === 'multi_checkbox'"
          :values="attr.attribute_values ?? []"
          :model-value="getVal(attr.attribute_key) || []"
          @update:model-value="setVal(attr.attribute_key, $event)"
        />

        <!-- Radio -->
        <RadioField
          v-else-if="attr.attribute_display_type === 'radio'"
          :values="attr.attribute_values ?? []"
          :model-value="getVal(attr.attribute_key) || ''"
          @update:model-value="setVal(attr.attribute_key, $event)"
        />
      </div>
    </div>
  </div>
</template>
